var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import ArrayMixin, { NativeArray } from '@ember/array';
import { assert } from '@ember/debug';
import { dependentKeyCompat } from '@ember/object/compat';
import { tracked } from '@glimmer/tracking';
import Ember from 'ember';
import { resolve } from 'rsvp';

/**
 @module @ember-data/model
 */
/**
  This class is returned as the result of accessing an async hasMany relationship
  on an instance of a Model extending from `@ember-data/model`.

  A PromiseManyArray is an array-like proxy that also proxies certain method calls
  to the underlying ManyArray in addition to being "promisified".

  Right now we proxy:

    * `reload()`
    * `createRecord()`

  This promise-proxy behavior is primarily to ensure that async relationship interact
  nicely with templates. In your JS code you should resolve the promise first.

  ```js
  const comments = await post.comments;
  ```

  @class PromiseManyArray
  @public
*/
let PromiseManyArray = (_class = class PromiseManyArray {
  constructor(promise, content) {
    //---- Methods/Properties on ArrayProxy that we will keep as our API
    _initializerDefineProperty(this, "content", _descriptor, this);
    //----  Properties/Methods from the PromiseProxyMixin that we will keep as our API
    /**
     * Whether the loading promise is still pending
     *
     * @property {boolean} isPending
     * @public
     */
    _initializerDefineProperty(this, "isPending", _descriptor2, this);
    /**
     * Whether the loading promise rejected
     *
     * @property {boolean} isRejected
     * @public
     */
    _initializerDefineProperty(this, "isRejected", _descriptor3, this);
    /**
     * Whether the loading promise succeeded
     *
     * @property {boolean} isFulfilled
     * @public
     */
    _initializerDefineProperty(this, "isFulfilled", _descriptor4, this);
    /**
     * Whether the loading promise completed (resolved or rejected)
     *
     * @property {boolean} isSettled
     * @public
     */
    _initializerDefineProperty(this, "isSettled", _descriptor5, this);
    this._update(promise, content);
    this.isDestroyed = false;
    this.isDestroying = false;
    const meta = Ember.meta(this);
    meta.hasMixin = mixin => {
      if (mixin === NativeArray || mixin === ArrayMixin) {
        return true;
      }
      return false;
    };
  }
  /**
   * Retrieve the length of the content
   * @property length
   * @public
   */
  get length() {
    // shouldn't be needed, but ends up being needed
    // for computed chains even in 4.x
    this['[]'];
    return this.content ? this.content.length : 0;
  }

  // ember-source < 3.23 (e.g. 3.20 lts)
  // requires that the tag `'[]'` be notified
  // on the ArrayProxy in order for `{{#each}}`
  // to recompute. We entangle the '[]' tag from
  get '[]'() {
    return this.content ? this.content['[]'] : this.content;
  }

  /**
   * Iterate the proxied content. Called by the glimmer iterator in #each
   *
   * @method forEach
   * @param cb
   * @returns
   * @private
   */
  forEach(cb) {
    this['[]']; // needed for < 3.23 support e.g. 3.20 lts
    if (this.content && this.length) {
      this.content.forEach(cb);
    }
  }
  /**
   * chain this promise
   *
   * @method then
   * @public
   * @param success
   * @param fail
   * @returns Promise
   */
  then(s, f) {
    return this.promise.then(s, f);
  }

  /**
   * catch errors thrown by this promise
   * @method catch
   * @public
   * @param callback
   * @returns Promise
   */
  catch(cb) {
    return this.promise.catch(cb);
  }

  /**
   * run cleanup after this promise completes
   *
   * @method finally
   * @public
   * @param callback
   * @returns Promise
   */
  finally(cb) {
    return this.promise.finally(cb);
  }

  //---- Methods on EmberObject that we should keep

  destroy() {
    this.isDestroying = true;
    this.isDestroyed = true;
    this.content = null;
    this.promise = null;
  }

  //---- Methods/Properties on ManyArray that we own and proxy to

  /**
   * Retrieve the links for this relationship
   * @property links
   * @public
   */
  get links() {
    return this.content ? this.content.links : undefined;
  }

  /**
   * Retrieve the meta for this relationship
   * @property meta
   * @public
   */
  get meta() {
    return this.content ? this.content.meta : undefined;
  }

  /**
   * Reload the relationship
   * @method reload
   * @public
   * @param options
   * @returns
   */
  reload(options) {
    assert('You are trying to reload an async manyArray before it has been created', this.content);
    this.content.reload(options);
    return this;
  }

  //---- Our own stuff

  _update(promise, content) {
    if (content !== undefined) {
      this.content = content;
    }
    this.promise = tapPromise(this, promise);
  }
  static create({
    promise,
    content
  }) {
    return new this(promise, content);
  }

  // Methods on ManyArray which people should resolve the relationship first before calling
  createRecord(...args) {
    assert('You are trying to createRecord on an async manyArray before it has been created', this.content);
    return this.content.createRecord(...args);
  }

  // Properties/Methods on ArrayProxy we should deprecate

  get firstObject() {
    return this.content ? this.content.firstObject : undefined;
  }
  get lastObject() {
    return this.content ? this.content.lastObject : undefined;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "content", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "length", [dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "length"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, '[]', [dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, '[]'), _class.prototype), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "isPending", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "isRejected", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "isFulfilled", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "isSettled", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "links", [dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "links"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "meta", [dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "meta"), _class.prototype), _class);
export { PromiseManyArray as default };
function tapPromise(proxy, promise) {
  proxy.isPending = true;
  proxy.isSettled = false;
  proxy.isFulfilled = false;
  proxy.isRejected = false;
  return resolve(promise).then(content => {
    proxy.isPending = false;
    proxy.isFulfilled = true;
    proxy.isSettled = true;
    proxy.content = content;
    return content;
  }, error => {
    proxy.isPending = false;
    proxy.isFulfilled = false;
    proxy.isRejected = true;
    proxy.isSettled = true;
    throw error;
  });
}
const EmberObjectMethods = ['addObserver', 'cacheFor', 'decrementProperty', 'get', 'getProperties', 'incrementProperty', 'notifyPropertyChange', 'removeObserver', 'set', 'setProperties', 'toggleProperty'];
EmberObjectMethods.forEach(method => {
  PromiseManyArray.prototype[method] = function delegatedMethod(...args) {
    return Ember[method](this, ...args);
  };
});
const InheritedProxyMethods = ['addArrayObserver', 'addObject', 'addObjects', 'any', 'arrayContentDidChange', 'arrayContentWillChange', 'clear', 'compact', 'every', 'filter', 'filterBy', 'find', 'findBy', 'getEach', 'includes', 'indexOf', 'insertAt', 'invoke', 'isAny', 'isEvery', 'lastIndexOf', 'map', 'mapBy', 'objectAt', 'objectsAt', 'popObject', 'pushObject', 'pushObjects', 'reduce', 'reject', 'rejectBy', 'removeArrayObserver', 'removeAt', 'removeObject', 'removeObjects', 'replace', 'reverseObjects', 'setEach', 'setObjects', 'shiftObject', 'slice', 'sortBy', 'toArray', 'uniq', 'uniqBy', 'unshiftObject', 'unshiftObjects', 'without'];
InheritedProxyMethods.forEach(method => {
  PromiseManyArray.prototype[method] = function proxiedMethod(...args) {
    assert(`Cannot call ${method} before content is assigned.`, this.content);
    return this.content[method](...args);
  };
});